import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AppSearchBox from "../../../../components/searchBox";
import GroupHierarchyListItem from "./groupHierarchyListItem";
import { AdminFormFieldSubmit } from "../../../../components/admin/formFields";
import GroupHierarchyAddGroupButton from "./groupHierarchyAddGroupButton";
import { useLazyOrgGHListGroupsQuery, useOrgGHListGroupsQuery } from "../../../../api/network/adminApiServices";
import svgIcons from "../../../../assets/svg/svgIcons";
import { useGHContext } from "../contexts/groupHierarchy.context";
import { GHListGroupsPayload } from "../../../../api/types/Group";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../../api/store/commonSlice";
import { APP_CONFIG } from "../../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../../admin-details/role/constants";
import { useHGSelectorContext } from "../../../../components/common/hierarchyGroupSelector.context";

type TGroupHierarchyViewProps = {
  group_id: string | null;
  level: number;
  searchEnabled?: boolean;
};

const GroupHierarchyLevel: FC<TGroupHierarchyViewProps> = ({
  group_id = null,
  level = 0,
  searchEnabled = true,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'admins.groups.hierarchy' });
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const {
    hierarchyOnDisplay,
    setHierarchyOnDisplay,
    movingGroup,
  } = useGHContext();

  const {
    hGSelectorMode,
  } = useHGSelectorContext();

  useEffect(() => {
    // bring into view
    listWrapperRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, []);

  const [listGroupsQueryParams, setListGroupsQueryParams] = useState<GHListGroupsPayload>({
    group_id: group_id === null ? undefined : group_id,
    page_size: APP_CONFIG.LISTINGS.GROUP_HIERARCHY_PAGE_SIZE,
  });
  useEffect(() => {
    setListGroupsQueryParams({
      ...listGroupsQueryParams,
      group_id: group_id === null ? undefined : group_id,
    });
  }, [group_id]);

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    // update lastPage to 1
    setHierarchyOnDisplay(
      [
        ...hierarchyOnDisplay.slice(0, level),
        {
          ...hierarchyOnDisplay[level],
          lastPage: 1,
        },
      ]
    );
    setListGroupsQueryParams({
      ...listGroupsQueryParams,
      search: !!value ? value : undefined,
    })
  }, 500);

  const {
    data: dataGHListGroups,
    isFetching: isFetchingGHListGroups,
    error: errorGHListGroups
  } = useOrgGHListGroupsQuery(
    listGroupsQueryParams,
    {
      skip:
        level === 0 &&
        modalsState.showReadOnlyViewForHierarchyGroup !== null,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (level === 0 && modalsState.showReadOnlyViewForHierarchyGroup !== null) {
      setHierarchyOnDisplay(
        [
          {
            group_id: modalsState.showReadOnlyViewForHierarchyGroup.groupid,
            level: 0,
            groups: [modalsState.showReadOnlyViewForHierarchyGroup],
            selectedGroup: null,
            lastPage: 1,
          },
        ]
      );
    } else {
      if (dataGHListGroups) {
        setHierarchyOnDisplay(
          [
            ...hierarchyOnDisplay.slice(0, level),
            {
              group_id,
              level,
              groups: dataGHListGroups.results,
              selectedGroup: null,
              lastPage: 1,
            },
          ]
        );
      }
    }
  }, [dataGHListGroups, modalsState.showReadOnlyViewForHierarchyGroup, level]);

  const [orgGHListGroupAPITrigger, { isLoading: isLoadingOrgGHListGroups }] = useLazyOrgGHListGroupsQuery();

  // ENABLE WHEN NEED ARISES
  // -----------------------
  // load more pages one-by-one on mount if lastPage > 1
  // useEffect(() => {
  //   if (hierarchyOnDisplay[level]?.lastPage && hierarchyOnDisplay[level]?.lastPage > 1) {
  //     for (let i = 2; i <= hierarchyOnDisplay[level]?.lastPage; i++) {
  //       orgGHListGroupAPITrigger({
  //         ...listGroupsQueryParams,
  //         page: i,
  //       })
  //         .unwrap()
  //         .then((response) => {
  //           setHierarchyOnDisplay(
  //             [
  //               ...hierarchyOnDisplay.slice(0, level),
  //               {
  //                 ...hierarchyOnDisplay[level],
  //                 groups: [
  //                   ...hierarchyOnDisplay[level].groups,
  //                   ...response.results,
  //                 ],
  //               },
  //             ]
  //           );
  //         });
  //     }
  //   }
  // }, []);

  const handleLoadMoreClick = () => {
    const nextPage = (hierarchyOnDisplay[level]?.lastPage || 1) + 1;
    orgGHListGroupAPITrigger({
      ...listGroupsQueryParams,
      page: nextPage,
    })
      .unwrap()
      .then((response) => {
        setHierarchyOnDisplay(
          [
            ...hierarchyOnDisplay.slice(0, level),
            {
              ...hierarchyOnDisplay[level],
              groups: [
                ...hierarchyOnDisplay[level].groups,
                ...response.results,
              ],
              lastPage: nextPage,
            },
          ]
        );
      });
  }

  return (
    <div className={`flex flex-col min-w-80`}>
      <div className="flex items-center justify-between min-h-16 px-3 font-semibold text-accent-blue-dark">
        <span>
          {level === 0
            ? t(modalsState.showReadOnlyViewForHierarchyGroup ? 'viewing_group' : 'parent_group')
            : t('sub_group_level', { level })
          }
        </span>
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (<span className="text-[10px] text-heading-gray888/70">{group_id}</span>)}
      </div>
      <div className={`flex-grow flex flex-col border-r border-r-black/10`}>
        <div className="p-3">
          <AppSearchBox
            placeholder={t('search_placeholder')}
            initialValue={""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => debouncedSetSearchKeyword(e.target.value)}
            onClear={() => debouncedSetSearchKeyword("")}
            disabled={
              !searchEnabled ||
              /* isFetchingGHListGroups || */
              (dataGHListGroups?.count === 0 && !!!listGroupsQueryParams.search) ||
              (modalsState.showReadOnlyViewForHierarchyGroup !== null && level === 0)
            }
          />
        </div>
        <div className="flex-grow relative">
          <div
            className="absolute top-0 left-0 w-full h-full overflow-y-auto"
            ref={listWrapperRef}
          >
            {isFetchingGHListGroups ? (
              <div className="flex items-center justify-center h-36">
                <img src={svgIcons.LoadingAnimation} alt="loading" className="size-16" />
              </div>
            ) : (
              <>
                {/* List of Groups */}
                {hierarchyOnDisplay[level]?.groups.map((groupItem, index) => (
                  <GroupHierarchyListItem
                    assetsCount={groupItem.active_inactive_vehicle_counts.active_vehicle_count + groupItem.active_inactive_vehicle_counts.inactive_vehicle_count}
                    title={groupItem.groupid}
                    key={`group_${level}_${index}`}
                    group_id={groupItem.groupid}
                    description={groupItem.description}
                    index={index}
                    level={level}
                    onDropdownOpen={() => {
                      // for items beyond index 5, scroll the list wrapper down by 160px
                      if (index <= 5) return;
                      listWrapperRef.current?.scrollBy({
                        left: 0,
                        top: (level === 0 ? 80 : 160) + 40,
                        behavior: 'smooth',
                      });
                    }}
                    parent_group_id={group_id}
                    selected={groupItem.groupid === hierarchyOnDisplay?.[level]?.selectedGroup}
                  />
                ))}

                {/* Add Group Button */}
                {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >= ROLE_ACCESS_LEVELS.ALL && !hGSelectorMode && (
                  <GroupHierarchyAddGroupButton
                    level={level}
                    onClick={() => {
                      dispatch(setModalsData({
                        ...modalsState,
                        createGroupUnder: hierarchyOnDisplay?.[level]?.group_id || "",
                        showCreateGroup: true
                      }))
                    }}
                  />
                )}

                {/* Load More Button */}
                {!!dataGHListGroups && dataGHListGroups?.count > hierarchyOnDisplay?.[level]?.groups?.length && (
                  <div className="p-3">
                    <AdminFormFieldSubmit
                      type="button"
                      label={t('load_more')}
                      onClick={handleLoadMoreClick}
                      customWrapperClass="w-auto"
                      disabled={isLoadingOrgGHListGroups}
                    />
                  </div>
                )}
                <span className="space block w-full h-36"></span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
export default GroupHierarchyLevel;